/**
 * Legacy Masterpieces
 * Shopify LM Reservation and Shared Profile System
 *
 * New profile:
 * Reserve one permanent LM ID and inject its QR URL.
 *
 * Shared profile:
 * Read compatible profiles from the current Shopify cart.
 * Reuse the selected LM ID and exact QR URL.
 *
 * Preserves:
 * - Existing WordPress reservation endpoint
 * - Customily QR injection
 * - Permanent 10-digit LM IDs
 * - Existing Shopify reservation keys
 *
 * Does not modify WordPress or Make.com.
 */

(function () {
  'use strict';

  const endpoint =
    'https://profile.legacymasterpieces.com/wp-json/legacy-masterpieces/v1/reserve-id';

  const btnSel = '#customily-cart-btn';

  const qrSel =
    '[name="properties[public_profile_url]"]';

  const selectorId = 'lm-profile-selector';

  const dropdownId = 'lm-existing-profile';

  const dropdownWrapId =
    'lm-existing-profile-wrap';

  const errorId = 'lm-profile-error';

  let reservationKey = null;

  let busy = false;

  let cartProfiles = new Map();


 /* ==========================================
   * PROFILE TYPE MAPPING
   *
   * Actual Shopify SKUs mapped to the
   * existing WordPress profile types.
   *
   * Matches Make module 8.
   * ========================================== */

  const skuTypes = {

    'sport':
      'nfc-sport-card',

    'business-service':
      'nfc-business-card',

    'business-creator':
      'nfc-content-creator-',

    'equestrian horse':
      'nfc-legacy-equest',

    'equestrian rider':
      'nfc-legacy-equestria',

    'performing arts':
      'nfc-legacy-performin',

    'pedigree':
      'nfc-pedigree',

    'pedigree handler':
      'nfc-ped'

  };


  const profileLabels = {

    'nfc-sport-card':
      'Sports',

    'nfc-business-card':
      'Business',

    'nfc-content-creator-':
      'Content Creator',

    'nfc-legacy-equest':
      'Equestrian',

    'nfc-legacy-equestria':
      'Equestrian Rider',

    'nfc-legacy-performin':
      'Performing Arts',

    'nfc-pedigree':
      'Pedigree',

    'nfc-ped':
      'Pedigree Handler'

  };


  function profileType(sku) {

    return skuTypes[
      String(sku || '').trim()
    ] || '';

  }


  /* ==========================================
   * ELEMENTS
   * ========================================== */

  function selector() {

    return document.getElementById(
      selectorId
    );

  }


  function dropdown() {

    return document.getElementById(
      dropdownId
    );

  }


  function selectedMode() {

    return selector()?.querySelector(
      'input[name="lm_profile_choice"]:checked'
    )?.value || '';

  }


  function showError(message) {

    const el = document.getElementById(
      errorId
    );

    if (!el) return;

    el.textContent = message;

    el.hidden = !message;

  }


  function getForm(btn) {

    const form = btn.closest(
      'form[action*="/cart/add"]'
    );

    if (form) return form;

    const buy = btn.closest(
      'buy-buttons'
    );

    if (buy) {

      const id = buy.getAttribute(
        'form'
      );

      if (id) {

        const linked =
          document.getElementById(id);

        if (linked) return linked;

      }

    }

    return document.querySelector(
      'form[action*="/cart/add"]'
    );

  }


  function currentSku(form) {

    const variant = form?.querySelector(
      '[name="id"]'
    )?.value || '';

    /*
     * The Liquid block supplies the
     * current product SKU.
     *
     * The variant check ensures this is
     * not silently used for a different
     * variant selected later.
     */

    const selected = selector();

    const sku =
      selected?.dataset.productSku || '';

    return String(sku).trim();

  }


  function makeKey(form) {

    const variant =
      form?.querySelector(
        '[name="id"]'
      )?.value || 'unknown';

    const rand =
      window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : Math.random()
            .toString(36)
            .slice(2) +
          Date.now();

    return (
      'lm-' +
      variant +
      '-' +
      Date.now() +
      '-' +
      rand
    );

  }


  /* ==========================================
   * WORDPRESS RESERVATION
   * ========================================== */

  async function reserve(key) {

    const response = await fetch(
      endpoint,
      {

        method: 'POST',

        headers: {

          'Content-Type':
            'application/json'

        },

        body: JSON.stringify({

          reservation_key: key

        })

      }
    );

    if (!response.ok) {

      throw new Error(
        'Reservation failed: ' +
        response.status
      );

    }

    const data =
      await response.json();

    if (
      !data.success ||
      !/^LM-\d{10}$/.test(
        data.lm_id || ''
      ) ||
      !data.qr_url
    ) {

      throw new Error(
        'Invalid LM reservation response'
      );

    }

    return data;

  }


  /* ==========================================
   * SHOPIFY CART
   * ========================================== */

  async function getCart() {

    const root =
      window.Shopify?.routes?.root ||
      '/';

    const response = await fetch(

      root + 'cart.js',

      {

        credentials: 'same-origin',

        cache: 'no-store',

        headers: {

          'Accept':
            'application/json'

        }

      }

    );

    if (!response.ok) {

      throw new Error(
        'Unable to read Shopify cart'
      );

    }

    return response.json();

  }


  function validProfile(item) {

    const props =
      item.properties || {};

    const lmId =
      String(
        props._lm_id || ''
      ).trim();

    const qrUrl =
      String(
        props._lm_qr_url ||
        props.public_profile_url ||
        ''
      ).trim();

    const type =
      profileType(
        item.sku
      );

    if (
      !/^LM-\d{10}$/.test(lmId) ||
      !type ||
      !qrUrl
    ) {

      return null;

    }

    /*
     * Verify the QR destination matches
     * the actual permanent LM ID.
     */

    const number =
      String(
        Number(
          lmId.slice(3)
        )
      );

    const expectedUrl =
      'https://profile.legacymasterpieces.com/q/' +
      number;

    if (
      qrUrl.replace(/\/$/, '') !==
      expectedUrl
    ) {

      return null;

    }

    const name =
      String(
        props.Name ||
        props.name ||
        item.product_title ||
        'Unnamed Profile'
      ).trim();

    return {

      lmId: lmId,

      qrUrl: qrUrl,

      type: type,

      name: name,

      reservationKey:
        String(
          props._lm_reservation_key ||
          ''
        ),

      label:
        name +
        ' — ' +
        (
          profileLabels[type] ||
          'Profile'
        ) +
        ' — ' +
        lmId

    };

  }


  /* ==========================================
   * POPULATE DROPDOWN
   * ========================================== */

  async function refreshProfiles() {

    const select =
      dropdown();

    if (!select) return;

    const previous =
      select.value;

    const sku =
      currentSku();

    const requiredType =
      profileType(sku);

    select.innerHTML = '';

    const placeholder =
      document.createElement(
        'option'
      );

    placeholder.value = '';

    placeholder.textContent =
      'Select a profile from your cart';

    select.appendChild(
      placeholder
    );

    cartProfiles = new Map();

    /*
     * Unknown SKUs must not be allowed
     * to share a potentially incompatible
     * digital profile.
     */

    if (!requiredType) {

      select.disabled = true;

      showError(
        'This product has not been configured for shared digital profiles. Please contact Legacy Masterpieces.'
      );

      return;

    }

    const cart =
      await getCart();

    /*
     * Multiple physical cards may already
     * share one LM ID.
     *
     * Display that digital profile only
     * once in the dropdown.
     */

    for (
      const item of cart.items || []
    ) {

      const profile =
        validProfile(item);

      if (!profile) continue;

      if (
        profile.type !==
        requiredType
      ) {

        continue;

      }

      if (
        cartProfiles.has(
          profile.lmId
        )
      ) {

        continue;

      }

      cartProfiles.set(
        profile.lmId,
        profile
      );

      const option =
        document.createElement(
          'option'
        );

      option.value =
        profile.lmId;

      option.textContent =
        profile.label;

      select.appendChild(
        option
      );

    }

    if (
      cartProfiles.has(
        previous
      )
    ) {

      select.value =
        previous;

    }

    select.disabled =
      cartProfiles.size === 0;

    if (
      cartProfiles.size === 0
    ) {

      showError(
        'There are no compatible digital profiles in your cart yet. Add the first card using Create a New Digital Profile.'
      );

    } else {

      showError('');

    }

  }


  /* ==========================================
   * SELECTOR INTERACTIONS
   * ========================================== */

  async function updateSelector() {

    const mode =
      selectedMode();

    const wrap =
      document.getElementById(
        dropdownWrapId
      );

    const select =
      dropdown();

    if (
      !wrap ||
      !select
    ) {

      return;

    }

    showError('');

    wrap.hidden =
      mode !== 'share';

    select.disabled =
      mode !== 'share';

    if (
      mode !== 'share'
    ) {

      select.value = '';

      return;

    }

    try {

      await refreshProfiles();

    } catch (err) {

      console.error(
        '[LM Cart Profiles]',
        err
      );

      select.disabled = true;

      showError(
        'Unable to read your cart. Please refresh this page and try again.'
      );

    }

  }


  document.addEventListener(
    'change',
    function (event) {

      if (
        event.target.matches(
          'input[name="lm_profile_choice"]'
        )
      ) {

        updateSelector();

      }

      if (
        event.target.matches(
          '[name="id"]'
        )
      ) {

        reservationKey = null;

        updateSelector();

      }

    }
  );


  /* ==========================================
   * CUSTOMILY AND SHOPIFY PROPERTIES
   * ========================================== */

  function setValue(
    element,
    value
  ) {

    element.value = value;

    element.dispatchEvent(
      new Event(
        'input',
        {
          bubbles: true
        }
      )
    );

    element.dispatchEvent(
      new Event(
        'change',
        {
          bubbles: true
        }
      )
    );

  }


  function setProp(
    form,
    name,
    value
  ) {

    let input =
      form.querySelector(

        '[name="properties[' +
        name +
        ']"]'

      );

    if (!input) {

      input =
        document.createElement(
          'input'
        );

      input.type = 'hidden';

      input.name =
        'properties[' +
        name +
        ']';

      form.appendChild(
        input
      );

    }

    input.value = value;

  }


  async function injectProfile(
    form,
    data,
    mode
  ) {

    const qrFields =
      document.querySelectorAll(
        qrSel
      );

    if (
      !qrFields.length
    ) {

      throw new Error(
        'Customily QR field not found'
      );

    }

    /*
     * Preserve the original working
     * Customily input/change behavior.
     */

    qrFields.forEach(
      function (field) {

        setValue(
          field,
          data.qrUrl
        );

      }
    );

    await new Promise(
      function (resolve) {

        setTimeout(
          resolve,
          50
        );

      }
    );

    const check =
      document.querySelector(
        qrSel
      );

    if (!check) {

      throw new Error(
        'Customily QR field disappeared'
      );

    }

    if (
      check.value !==
      data.qrUrl
    ) {

      console.warn(
        '[LM] Re-forcing Customily QR URL'
      );

      setValue(
        check,
        data.qrUrl
      );

    }

    /*
     * Permanent profile data.
     */

    setProp(
      form,
      '_lm_id',
      data.lmId
    );

    setProp(
      form,
      '_lm_qr_url',
      data.qrUrl
    );

    setProp(
      form,
      '_lm_reservation_key',
      data.reservationKey
    );

    /*
     * Preserve the selected profile action
     * in Shopify's line-item properties.
     */

    setProp(
      form,
      'Link to Profile',
      mode === 'share'
        ? 'Existing Online Profile'
        : 'New Online Profile'
    );

    setProp(
      form,
      '_lm_profile_action',
      mode === 'share'
        ? 'share'
        : 'new'
    );

    setProp(
      form,
      '_lm_shared_profile_id',
      mode === 'share'
        ? data.lmId
        : ''
    );

    setProp(
      form,
      '_lm_profile_type',
      profileType(
        currentSku(form)
      )
    );

  }


  /* ==========================================
   * ADD TO CART
   * ========================================== */

  document.addEventListener(

    'click',

    async function (event) {

      const btn =
        event.target.closest(
          btnSel
        );

      if (!btn) return;

      /*
       * Second click is the original
       * Customily Add to Cart action.
       */

      if (
        btn.dataset.lmReady ===
        '1'
      ) {

        delete btn.dataset.lmReady;

        return;

      }

      if (busy) {

        event.preventDefault();

        event.stopImmediatePropagation();

        return;

      }

      event.preventDefault();

      event.stopImmediatePropagation();

      const form =
        getForm(btn);

      if (!form) {

        showError(
          'Unable to prepare this card. Please refresh the page.'
        );

        return;

      }

      /*
       * Mandatory profile selection.
       */

      const mode =
        selectedMode();

      if (
        mode !== 'new' &&
        mode !== 'share'
      ) {

        showError(
          'Please select how this card should be connected before adding it to your cart.'
        );

        selector()?.scrollIntoView({

          behavior: 'smooth',

          block: 'center'

        });

        return;

      }

      const sku =
        currentSku(form);

      const type =
        profileType(sku);

      if (!type) {

        showError(
          'This product is not configured for digital profile ordering. Please contact Legacy Masterpieces.'
        );

        return;

      }

      busy = true;

      btn.disabled = true;

      showError('');

      try {

        let data;

        /*
         * NEW DIGITAL PROFILE
         */

        if (
          mode === 'new'
        ) {

          if (
            !reservationKey
          ) {

            reservationKey =
              makeKey(form);

          }

          const result =
            await reserve(
              reservationKey
            );

          data = {

            lmId:
              result.lm_id,

            qrUrl:
              result.qr_url,

            reservationKey:
              reservationKey

          };

        }

        /*
         * SHARE EXISTING CART PROFILE
         */

        else {

          const chosen =
            dropdown()?.value || '';

          if (!chosen) {

            throw new Error(
              'Please select the digital profile this card should share.'
            );

          }

          /*
           * Read the live cart again.
           * Do not rely on an old dropdown
           * selection after cart changes.
           */

          await refreshProfiles();

          const profile =
            cartProfiles.get(
              chosen
            );

          if (!profile) {

            throw new Error(
              'The selected profile is no longer available in your cart. Please select it again.'
            );

          }

          if (
            profile.type !==
            type
          ) {

            throw new Error(
              'These cards use different digital profile types and cannot share a profile.'
            );

          }

          data = {

            lmId:
              profile.lmId,

            qrUrl:
              profile.qrUrl,

            reservationKey:
              profile.reservationKey

          };

        }

        /*
         * Inject the verified final
         * profile destination.
         */

        await injectProfile(
          form,
          data,
          mode
        );

        /*
         * Release the original
         * Customily Add to Cart button.
         */

        btn.dataset.lmReady =
          '1';

        reservationKey = null;

        busy = false;

        btn.disabled = false;

        btn.click();

      } catch (err) {

        console.error(
          '[LM]',
          err
        );

        busy = false;

        btn.disabled = false;

        showError(
          err.message ||
          'Unable to prepare this card. Please try again.'
        );

      }

    },

    true

  );


  /* ==========================================
   * INITIALIZATION
   * ========================================== */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      updateSelector
    );

  } else {

    updateSelector();

  }

})();